import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UseSuspenseQueryOptions } from '@tanstack/react-query';
import { AxiosRequestConfig, AxiosError } from 'axios';

import { Stack, Typography } from '@mui/material';
import { Message, Notes, AccessTime } from '@mui/icons-material';

import { chatdate } from '@/utilities/dateUtils';

import { getRecent, useGetRecentSuspense, useGetThreadUserSuspense } from '@/generated/client/messages/messages';

import './ShowThreads.css';
import { provideTargetIcon } from '../common/IconProvider';
import { inactiveTargetStates } from '@/tmp/Target';
import { ctxMessages } from '@/contexts/MessagesCtx';
import { DetailedMessage, MessageThread } from '@/generated/model';
import ChatEmptyState from '@/shared/ui/chat/ChatEmptyState';

const config = {
  query: {
    networkMode: 'always',
    retry: (failureCount: number, error: AxiosError<unknown>) => {
      // 4XX hibanal nem erdemes ismetelni kiveve a 429
      if (error.code?.startsWith('4') && error.code !== '429') return false;
      return failureCount < 5;
    },
    retryDelay: 5000,
    refetchInterval: 60000,
  },
  axios: {
    timeout: 2000,
  },
} satisfies {
  query?: Partial<
    UseSuspenseQueryOptions<
      Awaited<ReturnType<typeof getRecent>>,
      AxiosError<unknown>,
      Awaited<ReturnType<typeof getRecent>>
    >
  >;
  axios?: AxiosRequestConfig;
};

function DisplayMessage({
  message,
  selected,
  onClick,
}: {
  message: DetailedMessage;
  selected: boolean;
  onClick: () => void;
}) {
  const { t, i18n } = useTranslation();
  const threadUser = message.toUsers[0].id !== -1 ? message.toUsers[0] : message.fromUser;

  function getThreadClass() {
    if (threadUser.state && inactiveTargetStates.includes(threadUser.state)) {
      return 'inactive-thread';
    }
    return '';
  }

  return (
    <div
      className={`thread-item ${selected ? 'selected-thread-item' : ''}`}
      onClick={onClick}
      key={message.id}
      data-qa-element-id="message_thread"
    >
      <div className="thread-header">
        <Stack direction="row" sx={{ mb: 1 }}>
          <Message />
          <Stack>
            <Typography
              variant="subtitle1"
              sx={{ lineHeight: 1, color: 'grey.800' }}
              className={getThreadClass()}
              data-qa-element-id="target_name_title"
            >
              {threadUser.name}
            </Typography>
            <Typography
              variant="helperText"
              sx={{ color: 'grey.600' }}
              className={getThreadClass()}
              data-qa-element-id="target_description"
            >
              {threadUser.description}
            </Typography>
          </Stack>
        </Stack>
        <div className="last-message-time" data-qa-element-id="last_message_time">
          <AccessTime />
          <Typography variant="body2">{chatdate(message.sentStatus.timeMicros, i18n.language, t)}</Typography>
        </div>
      </div>
      <Stack direction="row">
        <img className="chat-target-icon" src={provideTargetIcon(message.fromUser.category)} />
        <Typography variant="body2" sx={{ color: 'grey.600' }} data-qa-element-id="last_message_sender_title">
          {message.fromUser.name}
        </Typography>
      </Stack>
      <div className="last-message" data-qa-element-id="last_message">
        <Notes />
        <Typography variant="body2">{message.content.body}</Typography>
      </div>
    </div>
  );
}

function EmptyDisplayMessage({ onClick, messageThread }: { onClick: () => void; messageThread: MessageThread }) {
  const ctx = useContext(ctxMessages);
  const { t } = useTranslation();

  if (!ctx) {
    return;
  }

  return (
    <div className="thread-item" onClick={onClick} key={messageThread.id}>
      <div className="thread-header">
        <Stack direction="row" sx={{ mb: 1 }}>
          <Message />
          <Stack>
            <Typography variant="subtitle1" sx={{ lineHeight: 1, color: 'grey.800' }}>
              {messageThread.threadUser.name}
            </Typography>
            <Typography variant="helperText" sx={{ color: 'grey.600' }}>
              {messageThread.threadUser.description}
            </Typography>
          </Stack>
        </Stack>
      </div>
      <div className="last-message">
        <Notes />
        <Typography variant="body2">{t('chat.noMessage')}</Typography>
      </div>
    </div>
  );
}

function DisplayConversation({ conversation }: { conversation: MessageThread }) {
  const ctx = useContext(ctxMessages);
  if (!conversation) {
    return null;
  }

  if (!conversation.messages || !conversation.messages.length) {
    function emptyOnClickWrapper() {
      ctx?.setThread({ user: conversation.threadUser, threadId: conversation.id });
    }
    return <EmptyDisplayMessage onClick={emptyOnClickWrapper} messageThread={conversation} />;
  }

  function onClickWrapper() {
    ctx?.setThread({ user: conversation.threadUser, threadId: conversation.messages[0].threadId });
  }

  const selected = ctx?.thread?.threadId === conversation.id;
  return (
    <>
      {conversation.messages
        .sort((a, b) => b.sentStatus.timeMicros - a.sentStatus.timeMicros)
        .map((m) => (
          <DisplayMessage key={m.id} message={m} selected={selected} onClick={onClickWrapper} />
        ))}
    </>
  );
}

function ShowResult({ messages }: { messages: MessageThread[] | undefined }) {
  const container = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (container.current && container.current.parentElement) {
      container.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [container, messages]);

  if (!messages || !messages.length) {
    return <ChatEmptyState listType="channel" />;
  }

  function sortThreads(a: MessageThread, b: MessageThread) {
    const aHasMessage = a.messages.length;
    const bHasMessage = b.messages.length;
    if (!aHasMessage && !bHasMessage) {
      return compareNames(a, b);
    }
    if (!aHasMessage) {
      return 1;
    }
    if (!bHasMessage) {
      return -1;
    }
    const timeDiff = b.messages[0].sentStatus.timeMicros - a.messages[0].sentStatus.timeMicros;
    if (timeDiff !== 0) {
      return timeDiff;
    }
    return compareNames(a, b);
  }

  function compareNames(a: MessageThread, b: MessageThread) {
    return a.threadUser.name.localeCompare(b.threadUser.name);
  }

  return (
    <div ref={container}>
      {messages
        .sort((a, b) => sortThreads(a, b))
        .map((c) => (
          <DisplayConversation key={c.threadUser.name} conversation={c} />
        ))}
    </div>
  );
}

export function ShowRecentThreads() {
  const recent = useGetRecentSuspense(
    {
      messagesByThreadLimit: 1,
      threadLimit: 10,
    },
    config
  );

  return <ShowResult messages={recent.data?.data} />;
}

export function ShowUserThreads({ name }: { name: string }) {
  const recent = useGetThreadUserSuspense(
    {
      messagesByThreadLimit: 1,
      name,
    },
    config
  );

  return <ShowResult key={name} messages={recent.data?.data} />;
}
// uj cucc
// refresh
// lapozas
// iras
